import type { Fee, GeneralFee } from "@@/shared/financials/fees/fee";
import { feeIdZodSchema, type FeeId } from "@@/shared/financials/fees/fee-id";
import { feeSourceOrders } from "@@/shared/financials/fees/fee-sources/orders/fee-source-orders";
import { feeSourceTransactions } from "@@/shared/financials/fees/fee-sources/transactions/fee-source-transactions";
import type { Order } from "@@/shared/orders/order";
import { negatePrice } from "@@/shared/prices/negate-price";
import { priceFactory } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { Transaction } from "@@/shared/settlements/transactions";
import { repeat } from "@@/utilities/repeat";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    _ReportItemBase} from "../../shared/_report-item-base";
import {
    _reportItemBaseZodSchema,
} from "../../shared/_report-item-base";
import { reportItemIdFactory } from "../../shared/_report-item-id";

type FeeReportItem = _ReportItemBase & {
    readonly type: "FEE";
    readonly sourceId: FeeId;
};

const feeReportItemZodSchema: z.ZodSchema<FeeReportItem> = z.intersection(
    _reportItemBaseZodSchema,
    z.object({
        type: z.literal("FEE"),
        sourceId: feeIdZodSchema,
    }),
);

const feeReportItemFactory = (
    params: Except<FeeReportItem, "_id" | "_type" | "type" | "totalPrice">,
): FeeReportItem => {
    const totalPrice = sumPrices(repeat(params.pricePerItem, params.quantity));
    return {
        _id: reportItemIdFactory(),
        _type: "REPORT_ITEM",
        type: "FEE",
        sourceId: params.sourceId,
        title: params.title,
        totalPrice: totalPrice,
        pricePerItem: params.pricePerItem,
        quantity: params.quantity,
    };
};

const feeReportItemsFactory = (
    fees: Fee[],
    orders: Order[],
    transactions: Transaction[],
) => {
    return fees
        .filter(fee => fee.feeTarget === "provider")
        .flatMap(fee => {
            if (fee.type === "GENERAL_FEE") {
                switch (fee.feeSource.type) {
                    case "ORDERS": {
                        const filteredOrders = feeSourceOrders(
                            orders,
                            fee.feeSource,
                        );

                        const feePrice = filteredOrders.map(o => {
                            const price = priceFactory({
                                amountExcludingVat:
                                    calculatePercentageFeeForOrder(fee, o),
                                vat: 0.25,
                            });

                            const feeAmount = sumPrices([fee.flatFee, price]);
                            if (
                                fee.minValue &&
                                fee.minValue.amountExcludingVat >
                                    feeAmount.amountExcludingVat
                            ) {
                                return fee.minValue;
                            }

                            return negatePrice(feeAmount);
                        });

                        return feeReportItemFactory({
                            pricePerItem: sumPrices(feePrice),
                            sourceId: fee._id,
                            title: fee.title,
                            quantity: 1,
                        });
                    }
                    case "TRANSACTIONS": {
                        const filteredTransactions = feeSourceTransactions(
                            transactions,
                            fee.feeSource,
                        );

                        const feePrice = filteredTransactions.map(t => {
                            const price = priceFactory({
                                amountExcludingVat:
                                    calculatePercentageFeeForTransaction(
                                        fee,
                                        t,
                                    ),
                                vat: 0.25,
                            });

                            const feeAmount = sumPrices([fee.flatFee, price]);
                            if (
                                fee.minValue &&
                                fee.minValue.amountExcludingVat >
                                    feeAmount.amountExcludingVat
                            ) {
                                return fee.minValue;
                            }

                            return negatePrice(feeAmount);
                        });

                        return feeReportItemFactory({
                            pricePerItem: sumPrices(feePrice),
                            sourceId: fee._id,
                            title: fee.title,
                            quantity: 1,
                        });
                    }

                    default:
                        throw new Error(
                            "NOT IMPLEMENTED " + fee.feeSource.type,
                        );
                }
            }
            //Value Fee
            return feeReportItemFactory({
                pricePerItem: negatePrice(fee.price),
                sourceId: fee._id,
                title: fee.title,
                quantity: 1,
            });
        });
};

function calculatePercentageFeeForTransaction(
    fee: GeneralFee,
    transaction: Transaction,
) {
    if (fee.percentage) {
        const amount = fee.percentage.includeVatAmount
            ? transaction.transactionAmount //INCL_VAT
            : transaction.transactionAmount - transaction.vatAmount; //EXCL_VAT

        return amount * fee.percentage.percentage;
    }
    return 0;
}

function calculatePercentageFeeForOrder(fee: GeneralFee, order: Order) {
    if (fee.percentage) {
        const amount = fee.percentage.includeVatAmount
            ? order.totalPrice.amountIncludingVat //INCL_VAT
            : order.totalPrice.amountExcludingVat;

        return amount * fee.percentage.percentage;
    }
    return 0;
}

export { feeReportItemFactory, feeReportItemsFactory, feeReportItemZodSchema };
export type { FeeReportItem };
